// 1. Core & External Libraries
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import {
  useNavigation,
  useRoute,
  type CompositeNavigationProp,
  type RouteProp,
} from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { format, isSameMonth, parseISO, startOfDay, startOfMonth } from 'date-fns';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshControl, ScrollView, type LayoutChangeEvent } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

// 2. Layout & shared UI
import { Scroll, Stack } from '@/components/layout';
import { Skeleton } from '@/components/ui';

// 3. Feature (guardian)
import AddPlayerEmptyCard from '@/features/guardian/components/add-player-empty-card';
import { EventDetailTabletPanel } from '@/features/guardian/components/event-detail';
import FamilyAgendaSection from '@/features/guardian/components/family-agenda-section';
import FamilyCalendar from '@/features/guardian/components/family-calendar';
import FamilyDayPanel from '@/features/guardian/components/family-day-panel';
import FamilyHeader from '@/features/guardian/components/family-header';
import FamilyKidCard from '@/features/guardian/components/family-kid-card';
import { useEventDetailPanel } from '@/features/guardian/hooks/use-event-detail-panel';
import { useFamilyEvents } from '@/features/guardian/hooks/use-family-events';
import { useGuardianNotificationBadge } from '@/features/guardian/hooks/use-guardian-notifications';
import { useOpenEventDetail } from '@/features/guardian/hooks/use-open-event-detail';
import { toFamilyKid } from '@/features/guardian/utils/family-kids';

// 4. Hooks, stores, theme, types
import { FLOATING_TAB_BAR_CLEARANCE_TOKEN } from '@/components/ui/tabbar';
import { createStyles, useScaling } from '@/hooks';
import type { GuardianStackParamList, GuardianTabNavigatorParamList } from '@/navigations/types';
import { guardianColors } from '@/theme/colors';

type KidsNavigation = CompositeNavigationProp<
  BottomTabNavigationProp<GuardianTabNavigatorParamList, 'Kids'>,
  NativeStackNavigationProp<GuardianStackParamList>
>;

type GuardianKidsTabletBodyProps = {
  requestedEventId?: string;
  onRequestedEventHandled: () => void;
};

const TABLET_KID_CARD_GAP = 20;
/** Skeleton-only placeholder height; real cards size themselves. */
const TABLET_KID_CARD_SKELETON_HEIGHT = 93;
/** Pull-to-refresh state shared by the phone and tablet bodies: both refetch the
 *  guardian players query. */
function useKidsRefresh(refetch: () => Promise<unknown>) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);

  return { isRefreshing, handleRefresh };
}

const KidCardSkeleton = memo(() => {
  const { isTablet, scale } = useScaling();

  if (isTablet) {
    return (
      <Stack direction="row" gap={scale(TABLET_KID_CARD_GAP)}>
        <Stack flex={1}>
          <Skeleton
            width="100%"
            height={scale(TABLET_KID_CARD_SKELETON_HEIGHT)}
            borderRadius={scale(12)}
          />
        </Stack>
        <Stack flex={1}>
          <Skeleton
            width="100%"
            height={scale(TABLET_KID_CARD_SKELETON_HEIGHT)}
            borderRadius={scale(12)}
          />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap={scale(12)}>
      <Skeleton width="100%" height={scale(112)} borderRadius={scale(16)} />
      <Skeleton width="100%" height={scale(112)} borderRadius={scale(16)} />
    </Stack>
  );
});
KidCardSkeleton.displayName = 'KidCardSkeleton';

function GuardianKidsTabletBody({
  requestedEventId,
  onRequestedEventHandled,
}: GuardianKidsTabletBodyProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<KidsNavigation>();
  const { players: guardianPlayers, events, isLoading } = useFamilyEvents();

  const [today] = useState(() => startOfDay(new Date()));
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(today));
  const [selectedDate, setSelectedDate] = useState(() => today);
  const [kidListWidth, setKidListWidth] = useState(0);
  const [columnsWidth, setColumnsWidth] = useState(0);
  const columnGap = scale(20);
  const detailPanel = useEventDetailPanel(columnsWidth, columnGap);
  const openDetailPanel = detailPanel.open;
  const handleEventPress = useOpenEventDetail({ onInlineOpen: openDetailPanel });

  useEffect(() => {
    if (!requestedEventId || isLoading) return;

    const requestedEvent = events.find(event => event.id === requestedEventId);
    if (requestedEvent) {
      const eventDate = startOfDay(parseISO(requestedEvent.date));
      setVisibleMonth(startOfMonth(eventDate));
      setSelectedDate(eventDate);
      openDetailPanel(requestedEvent);
    }

    onRequestedEventHandled();
  }, [events, isLoading, onRequestedEventHandled, openDetailPanel, requestedEventId]);

  const kids = useMemo(() => guardianPlayers.map(toFamilyKid), [guardianPlayers]);

  const visibleEvents = useMemo(
    () => events.filter(event => isSameMonth(parseISO(event.date), visibleMonth)),
    [events, visibleMonth],
  );
  const selectedDateKey = format(selectedDate, 'yyyy-MM-dd');
  const selectedEvents = useMemo(
    () => events.filter(event => event.date === selectedDateKey),
    [events, selectedDateKey],
  );
  const kidCardWidth = useMemo(() => {
    if (kidListWidth <= 0) return 0;
    if (kids.length <= 1) return kidListWidth;
    return (kidListWidth - scale(TABLET_KID_CARD_GAP)) / 2;
  }, [kidListWidth, kids.length, scale]);
  const handleKidPress = useCallback(
    (playerId: string) => {
      const player = guardianPlayers.find(item => item.id === playerId);
      if (!player) return;
      navigation.navigate('PlayerDetail', { player });
    },
    [guardianPlayers, navigation],
  );

  const handleAddPlayerPress = useCallback(() => {
    navigation.navigate('AddPlayer');
  }, [navigation]);

  const handleKidListLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setKidListWidth(previousWidth => (previousWidth === width ? previousWidth : width));
  }, []);

  const handleColumnsLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setColumnsWidth(previousWidth => (previousWidth === width ? previousWidth : width));
  }, []);

  return (
    <Stack
      flex={1}
      paddingX={scale(20)}
      paddingTop={scale(16)}
      paddingBottom={insets.bottom}
      gap={scale(16)}>
      {isLoading ? (
        <KidCardSkeleton />
      ) : kids.length === 0 ? (
        <AddPlayerEmptyCard onPress={handleAddPlayerPress} />
      ) : (
        <Stack onLayout={handleKidListLayout}>
          {kidListWidth > 0 ? (
            <Scroll
              horizontal
              gap={scale(TABLET_KID_CARD_GAP)}
              showsHorizontalScrollIndicator={false}>
              {kids.map(kid => (
                <Stack key={kid.id} width={kidCardWidth}>
                  <FamilyKidCard kid={kid} onPress={handleKidPress} />
                </Stack>
              ))}
            </Scroll>
          ) : null}
        </Stack>
      )}

      <Stack flex={1} direction="row" onLayout={handleColumnsLayout}>
        <Animated.View
          style={[
            styles.tabletColumn,
            columnsWidth > 0 ? detailPanel.calendarStyle : styles.tabletCalendarFallback,
          ]}>
          <ScrollView
            style={styles.tabletCalendarScroll}
            contentContainerStyle={styles.tabletCalendarScrollContent}
            showsVerticalScrollIndicator={false}>
            <FamilyCalendar
              month={visibleMonth}
              selectedDate={selectedDate}
              today={today}
              events={visibleEvents}
              onChangeMonth={setVisibleMonth}
              onSelectDay={setSelectedDate}
            />
          </ScrollView>
        </Animated.View>
        <Animated.View
          style={[
            styles.tabletColumn,
            styles.tabletDayColumn,
            columnsWidth > 0 ? detailPanel.dayPanelStyle : styles.tabletDayFallback,
          ]}>
          <FamilyDayPanel
            selectedDate={selectedDate}
            events={selectedEvents}
            onEventPress={handleEventPress}
          />
        </Animated.View>
        {detailPanel.event ? (
          <Animated.View
            style={[styles.tabletColumn, styles.tabletDetailColumn, detailPanel.detailStyle]}>
            <EventDetailTabletPanel
              event={detailPanel.event}
              contentWidth={detailPanel.widths.detailOpen}
              onClose={detailPanel.close}
            />
          </Animated.View>
        ) : null}
      </Stack>
    </Stack>
  );
}

function GuardianKidsBody() {
  const { scale } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();
  const navigation = useNavigation<KidsNavigation>();
  const openEventDetail = useOpenEventDetail();

  const { players: guardianPlayers, events, isLoading, refetch } = useFamilyEvents();
  const { isRefreshing, handleRefresh } = useKidsRefresh(refetch);

  const kids = useMemo(() => guardianPlayers.map(toFamilyKid), [guardianPlayers]);

  const handleKidPress = useCallback(
    (playerId: string) => {
      const player = guardianPlayers.find(item => item.id === playerId);
      if (!player) return;
      navigation.navigate('PlayerDetail', { player });
    },
    [guardianPlayers, navigation],
  );

  const handleCalendarPress = useCallback(() => {
    navigation.navigate('GuardianFamilyCalendar');
  }, [navigation]);

  const handleAddPlayerPress = useCallback(() => {
    navigation.navigate('AddPlayer');
  }, [navigation]);

  return (
    <Scroll
      style={styles.scroll}
      showsVerticalScrollIndicator={false}
      refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={handleRefresh} />}
      contentContainerStyle={{ paddingBottom: insets.bottom + scale(40) }}>
      <Stack paddingX={scale(20)} paddingTop={scale(8)} gap={scale(8)}>
        {isLoading ? (
          <KidCardSkeleton />
        ) : kids.length === 0 ? (
          <AddPlayerEmptyCard onPress={handleAddPlayerPress} />
        ) : (
          kids.map(kid => <FamilyKidCard key={kid.id} kid={kid} onPress={handleKidPress} />)
        )}
      </Stack>

      <FamilyAgendaSection
        items={events}
        onItemPress={openEventDetail}
        onCalendarPress={handleCalendarPress}
        showCalendarLink={isLoading || kids.length > 0}
        showEmptyState={!isLoading && kids.length === 0}
      />
    </Scroll>
  );
}

/** Kids tab: mobile renders the family roster + agenda; tablet renders the
 *  interactive calendar and selected-day detail layout. */
export function GuardianKidsScreen() {
  const { isTablet } = useScaling();
  const navigation = useNavigation<KidsNavigation>();
  const route = useRoute<RouteProp<GuardianTabNavigatorParamList, 'Kids'>>();
  const hasUnreadNotification = useGuardianNotificationBadge();

  const handleRequestedEventHandled = useCallback(() => {
    navigation.setParams({ eventId: undefined });
  }, [navigation]);

  const handleNotificationPress = useCallback(() => {
    navigation.navigate('GuardianNotifications');
  }, [navigation]);

  return (
    <Stack flex={1} backgroundColor={guardianColors['1']}>
      <FamilyHeader
        hasUnreadNotification={hasUnreadNotification}
        onNotificationPress={handleNotificationPress}
      />
      {isTablet ? (
        <GuardianKidsTabletBody
          requestedEventId={route.params?.eventId}
          onRequestedEventHandled={handleRequestedEventHandled}
        />
      ) : (
        <GuardianKidsBody />
      )}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  scroll: {
    flex: 1,
  },
  tabletCalendarScroll: {
    flex: 1,
  },
  tabletCalendarScrollContent: {
    flexGrow: 1,
    paddingBottom: scale(FLOATING_TAB_BAR_CLEARANCE_TOKEN),
  },
  tabletColumn: {
    flexShrink: 0,
    minWidth: 0,
    height: '100%',
    overflow: 'hidden',
  },
  tabletCalendarFallback: {
    flexGrow: 2.05,
    flexBasis: 0,
  },
  tabletDayColumn: {
    marginLeft: scale(20),
    borderRadius: scale(16),
  },
  tabletDayFallback: {
    flexGrow: 1,
    flexBasis: 0,
  },
  tabletDetailColumn: {
    borderRadius: scale(16),
  },
}));

export default GuardianKidsScreen;
